/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-25 11:24:08                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2024-12-10 16:58:42                               *
 *****************************************************************************/

/* SUMMARY
  * paginate: paginate data
  * setSelectList: Prepare data to be printed in select
  * embeddedFormChange - handle embedded form change
*/
import { format } from "date-fns";
import { t } from "i18next";

/**
* paginate: paginate data
* @param array - data to paginate
* @param page_number - number of page
* @param page_size - data length in page
* @return - paginated data 
*/
export const paginate = ((array: any[], page_number: number, page_size: number) => {    
  const index = page_number * page_size;
  return array.slice(index, index+page_size);
});
/***/


/**
* setSelectList: Prepare data to be printed in select
* @param data - data list
* @param setLabel - set label function
* @return - prepared data list 
*/
export const setSelectList = <T>(data: T[], setLabel: (el: T) => string): EasyTech.IFormData<T>[] => {
  return data.map(el => ({value: el, label: setLabel(el)}));
};
/***/

/**
* embeddedFormChange - handle embedded form change
* @param name - field name changed
* @param value - field value changed 
*/
export function embeddedFormChange(
  data: any, 
  field: string, 
  name: string, 
  value: any,
  onChange: (name: string, value: any) => void
) {
  if (!data) data = {};
  if (!data[field]) data[field] = {};
  
  data[field][name] = value;
  onChange(field, data[field]);
}
/***/

/**
* startOfDay - get the start Date of day from date
* @param date - date
*/
export function startOfDay(date: Date){
  const d = new Date(date);
  d.setUTCHours(0,0,0,0);
  return d;
}

/**
* startOfWeek - get the start Date of week from date
* @param date - date
*/
export function startOfWeek(date: Date){
  var diff = date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1);
  const d = new Date(date);
  d.setDate(diff);
  d.setUTCHours(0,0,0,0);
  return d;
}

/**
* startOfMonth - get the start Date of month from date
* @param date - date
*/
export function startOfMonth(date = new Date()) {
  let d = startOfWeek(date);
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1));
  return startOfWeek(d);
}

export function toSinceString(date: number) {
  var seconds = Math.floor((new Date().getTime() - date) / 1000);
  return secondsToPartialString(seconds);
}

export function secondsToPartialString(seconds: number) {
  var interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + " "+t("date:years_mixed");
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + " "+t("date:months_mixed");
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + " "+t("date:day_mixed");
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + " "+t("date:hour_mixed");
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + " "+t("date:minute_mixed");
  }
  return Math.floor(seconds) + " "+t("date:second_mixed");
}

export function secondsToStringTime(sec_num: number)  {
  var hours   = Math.floor(sec_num / 3600);
  var minutes = Math.floor((sec_num - (hours * 3600)) / 60);

  let ret = "";
  if (hours > 0) {
    ret += (hours < 10) ? "0"+hours : hours;
    ret += " "+t("date:hour_mixed")+" ";
  }
  ret += (minutes   < 10) ? "0"+minutes : minutes;
  ret += " "+t("date:minute_mixed");

  return ret;
}


export function isEmpty(obj: any) {
  for (var prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }

  return true;
}

/**
* normalizeText - Remove diacritics and convert to lowercase
* For search with polish characters like ą, ć, ę, ł, ń, ó, ś, ź, ż
* @param text - text to normalize
* @return - normalized text
*/
export function normalizeText(text: string): string {
  if (!text) return "";
  
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[łŁ]/g, "l") // Polish L with stroke
    .replace(/[đĐ]/g, "d") 
    .trim();
}

export function compareObject(a: object | undefined, b: object | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Liste des realms disponibles
 */
export const REALMS = {
  POLAND: "pl",
  ROMANIA: "ro",
  BELGIUM: "be",
  LUXEMBOURG: "lu"
} as const;

export type RealmCode = typeof REALMS[keyof typeof REALMS];

/**
 * getCurrentRealm - Récupère le realm actuel depuis le localStorage
 * @returns Le code du realm actuel ou null si non défini
 */
export function getCurrentRealm(): RealmCode | null {
  return localStorage.getItem("realm") as RealmCode | null;
}

/**
 * isRealm - Vérifie si le realm actuel correspond à un realm spécifique
 * @param realm - Le code du realm à vérifier
 * @returns true si le realm actuel correspond
 */
export function isRealm(realm: RealmCode): boolean {
  return getCurrentRealm() === realm;
}

/**
 * isPolandRealm - Vérifie si le realm actuel est la Pologne
 * @returns true si le realm est la Pologne
 */
export function isPolandRealm(): boolean {
  return isRealm(REALMS.POLAND);
}

/**
 * isRomaniaRealm - Vérifie si le realm actuel est la Roumanie
 * @returns true si le realm est la Roumanie
 */
export function isRomaniaRealm(): boolean {
  return isRealm(REALMS.ROMANIA);
}

/**
 * isBelgiumRealm - Vérifie si le realm actuel est la Belgique
 * @returns true si le realm est la Belgique
 */
export function isBelgiumRealm(): boolean {
  return isRealm(REALMS.BELGIUM);
}

/**
 * isLuxembourgRealm - Vérifie si le realm actuel est le Luxembourg
 * @returns true si le realm est le Luxembourg
 */
export function isLuxembourgRealm(): boolean {
  return isRealm(REALMS.LUXEMBOURG);
}

/**
 * formatLastDiag - an Auto Diag submission date as "21/08/2026 - 11:04"
 * @param value - ISO date string, or null/undefined when the technician has never submitted
 * @return - the formatted date, or null when there is nothing to format
 */
export function formatLastDiag(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : format(date, "dd/MM/yyyy - HH:mm");
}

/**
 * parseApiDate - turn whatever the API sent into a Date, or null
 *
 * Most of this API sends dates as ISO strings or as epoch **milliseconds**, and a bare
 * `new Date(value)` handles both. Java `Instant` fields are the exception: Jackson's default
 * writes them as epoch **seconds**, optionally with a nanosecond fraction
 * (`1787571000.000000000`), and `new Date()` reads any number as milliseconds. A seconds value
 * read as milliseconds lands in January 1970 — which is exactly how the safety-diag report tab
 * came to show "21/01/1970" for Started and Sealed, both collapsing to the same second because a
 * multi-minute run shrinks to a few hundred milliseconds (ClickUp 86cbcf4ne).
 *
 * The backend now pins those fields to ISO-8601 per DTO, so this is belt and braces — but the
 * declared TypeScript types are not validated at runtime anywhere (responses are cast from
 * `unknown`), so a number can always reappear without the compiler noticing.
 *
 * The threshold: 1e11 milliseconds is March 1973, and 1e11 seconds is the year 5138. Any real
 * timestamp below it is therefore seconds, and any above it is milliseconds.
 *
 * @param value - ISO string, epoch seconds, or epoch milliseconds; null/undefined when absent
 * @return - the parsed Date, or null when there is nothing valid to parse
 */
export function parseApiDate(value?: string | number | null): Date | null {
  if (value === null || value === undefined || value === "") return null;

  const date =
    typeof value === "number"
      ? new Date(value < 1e11 ? Math.round(value * 1000) : value)
      : new Date(value);

  return isNaN(date.getTime()) ? null : date;
}

/**
 * formatApiDateTime - an API date as "24/08/2026 - 15:30"
 *
 * The house format, matching {@link formatLastDiag} and the Auto Diag display convention. Accepts
 * the same shapes {@link parseApiDate} does.
 *
 * @param value - ISO string, epoch seconds, or epoch milliseconds
 * @param fallback - what to render when there is nothing to show
 * @return - the formatted date, or the fallback
 */
export function formatApiDateTime(
  value?: string | number | null,
  fallback = "—",
): string {
  const date = parseApiDate(value);
  return date ? format(date, "dd/MM/yyyy - HH:mm") : fallback;
}

/**
 * formatRoles - Format and translate roles
 * @param roles - roles list
 * @return - formatted roles string
 */
export function formatRoles(roles: string[]): string {
  if (!roles || roles.length === 0) return "-";

  const translatedRoles = roles
    .filter((role) => role.startsWith("ROLE_"))
    .map((role) => t(`user:form.fields.roles.options.${role}`))
    .filter((translated) => !!translated && !translated.startsWith("user:form.fields.roles.options."));

  // Dédoublonnage
  const uniqueRoles = Array.from(new Set(translatedRoles));

  return uniqueRoles.length > 0 ? uniqueRoles.join(", ") : "-";
}